<template>
  <layout-header />
  <layout-sidebar />

  <div class="page-wrapper">
    <div class="content">
      <!-- Breadcrumb -->
      <div
        class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3"
      >
        <breadcrumb :title="title" :text="text" :text1="text1" />

        <div class="d-flex my-xl-auto right-content align-items-center flex-wrap dq-page-actions">
          <div class="mb-2">
            <div class="dropdown">
              <a
                class="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                data-bs-toggle="dropdown"
              >
                <i class="ti ti-file-export me-1"></i>Export
              </a>
              <ul class="dropdown-menu dropdown-menu-end p-3">
                <li>
                  <a
                    class="dropdown-item rounded-1"
                    @click.prevent="exportCsv"
                    v-can="'Reports.Export'"
                  >
                    <i class="ti ti-file-type-xls me-1"></i>Export as CSV
                  </a>
                </li>
                <li>
                  <a
                    class="dropdown-item rounded-1"
                    @click.prevent="exportPdf"
                    v-can="'Reports.Export'"
                  >
                    <i class="ti ti-file-type-pdf me-1"></i>Export as PDF
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div class="head-icons ms-2">
            <a id="collapse-header" @click="toggleHeader">
              <i class="ti ti-chevrons-up"></i>
            </a>
          </div>
        </div>
      </div>
      <!-- /Breadcrumb -->

      <div class="row">
        <!-- KPI -->
        <div class="col-xl-6 d-flex">
          <div class="row flex-fill dq-stat-grid">
            <div v-for="c in kpiCards" :key="c.title" class="col-md-6 d-flex">
              <div class="card flex-fill dq-stat-card">
                <div class="card-body">
                  <div
                    class="d-flex align-items-center justify-content-between bg-light border rounded p-2 mb-2"
                  >
                    <div>
                      <span class="fs-14 fw-normal text-truncate mb-1">{{
                        c.title
                      }}</span>
                      <h5 v-if="c.type === 'money'">
                        {{ currency }}
                        {{
                          Number(c.value).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                          })
                        }}
                      </h5>
                      <h5 v-else>{{ c.value }}</h5>
                    </div>
                    <a
                      href="javascript:void(0);"
                      class="avatar avatar-md avatar-rounded"
                      :class="`bg-transparent-${c.cls} border border-${c.cls}`"
                    >
                      <span :class="`text-${c.cls}`"
                        ><i class="ti ti-pig-money"></i
                      ></span>
                    </a>
                  </div>
                  <p class="fs-12 fw-normal d-flex align-items-center text-truncate mb-0">
                    <span class="text-muted">Based on selected period</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Filter -->
        <div class="col-xl-6 d-flex">
          <div class="card flex-fill">
            <div class="card-header border-0 pb-0">
              <div class="d-flex flex-wrap justify-content-between align-items-center">
                <div class="d-flex align-items-center">
                  <span class="me-2"><i class="ti ti-calendar-stats text-primary"></i></span>
                  <h5>Filter Period</h5>
                </div>

                <div class="input-icon-end position-relative" style="min-width: 230px">
                  <input
                    type="text"
                    class="form-control date-range bookingrange"
                    ref="dateRangeInput"
                    placeholder="dd/mm/yyyy - dd/mm/yyyy"
                  />
                  <span class="input-icon-addon"><i class="ti ti-chevron-down"></i></span>
                </div>
              </div>
            </div>

            <div class="card-body py-3">
              <div class="row g-2 align-items-end">
                <!-- <div class="col-12 col-md-6">
                  <label class="form-label mb-1">Savings Type (optional)</label>
                  <select class="form-select" v-model="filters.type" @change="onFiltersChanged">
                    <option value="">All</option>
                    <option value="normal_savings">Normal Savings</option>
                    <option value="pension">Pension</option>
                    <option value="insurance">Insurance</option>
                    <option value="other">Other</option>
                  </select>
                </div> -->

                <div class="col-12 col-md-6">
                  <p class="mb-0 text-muted">
                    Showing savings transactions between
                    <strong>{{ filters.from }}</strong> and <strong>{{ filters.to }}</strong>.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Table -->
      <div class="card">
        <div class="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
          <h5>Member Savings Report</h5>
        </div>
        <div class="card-body p-0">
          <div class="custom-datatable-filter table-responsive">
            <MemberSavingsReportTable
              :rows="store.memberSavingsRows"
              :loading="store.memberSavingsLoading"
              :currency="currency"
              :total="store.memberSavingsTotal"
              :page-size="store.memberSavingsPageSize"
              :current="store.memberSavingsCurrent"
              @change="onTableChange"
            />
          </div>
        </div>
      </div>
    </div>

    <div
      class="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3"
    >
      <p class="mb-0">2014 - {{ new Date().getFullYear() }} &copy; DoneQuick HR & Payroll</p>
      <p>
        Powered by <a href="#" @click.prevent class="text-primary">DoneQuick Technological World</a>
      </p>
    </div>
  </div>
</template>

<script setup>
import "daterangepicker/daterangepicker.css";
import "daterangepicker/daterangepicker.js";
import DateRangePicker from "daterangepicker";
import moment from "moment";
import { ref, reactive, onMounted, computed } from "vue";
import { useEmployeeSalaryStore } from "@/stores/employeeSalaryStore";
import MemberSavingsReportTable from "./member-savings-report-table.vue";

const title = "Member Savings Report";
const text = "HR";
const text1 = "Savings";

const store = useEmployeeSalaryStore();
const dateRangeInput = ref(null);

const filters = reactive({
  from: moment().startOf("year").format("YYYY-MM"),
  to: moment().format("YYYY-MM"),
  type: "",
  page: 1,
  per_page: 25,
});

function toggleHeader() {
  document.getElementById("collapse-header")?.classList.toggle("active");
  document.body.classList.toggle("header-collapse");
}

function initRangePicker() {
  if (!dateRangeInput.value) return;

  const start = moment(filters.from, "YYYY-MM").startOf("month");
  const end = moment(filters.to, "YYYY-MM").endOf("month");

  const onPick = (s, e) => {
    filters.from = moment(s).format("YYYY-MM");
    filters.to = moment(e).format("YYYY-MM");
    filters.page = 1;
    fetchReport();
  };

  new DateRangePicker(
    dateRangeInput.value,
    {
      startDate: start,
      endDate: end,
      showDropdowns: true,
      autoApply: true,
      ranges: {
        "This Month": [moment().startOf("month"), moment().endOf("month")],
        "Year to Date": [moment().startOf("year"), moment().endOf("month")],
        "Last Year": [
          moment().subtract(1, "year").startOf("year"),
          moment().subtract(1, "year").endOf("year"),
        ],
      },
    },
    onPick
  );
}

async function fetchReport() {
  await store.fetchMemberSavingsReport({
    from: filters.from,
    to: filters.to,
    type: filters.type || undefined,
    page: filters.page,
    per_page: filters.per_page,
  });
}

function onFiltersChanged() {
  filters.page = 1;
  fetchReport();
}

function onTableChange(pagination) {
  filters.page = pagination.current;
  filters.per_page = pagination.pageSize;
  fetchReport();
}

function exportCsv() {
  store.exportMemberSavingsReport("csv", {
    from: filters.from,
    to: filters.to,
    type: filters.type || undefined,
  });
}

function exportPdf() {
  store.exportMemberSavingsReport("pdf", {
    from: filters.from,
    to: filters.to,
    type: filters.type || undefined,
  });
}

const summary = computed(() => store.memberSavingsSummary || {});
const currency = computed(() => summary.value.currency || "KES");

const kpiCards = computed(() => [
  {
    title: "Total Deposits",
    value: summary.value.total_deposits || 0,
    cls: "success",
    type: "money",
  },
  {
    title: "Transactions",
    value: summary.value.transactions_count || 0,
    cls: "primary",
    type: "count",
  },
  {
    title: "Employees",
    value: summary.value.employee_count || 0,
    cls: "warning",
    type: "count",
  },
  {
    title: "Period",
    value: `${summary.value.from || filters.from} → ${summary.value.to || filters.to}`,
    cls: "info",
    type: "count",
  },
]);

onMounted(() => {
  initRangePicker();
  fetchReport();
});
</script>

<style scoped>
@media (max-width: 767.98px) {
  .dq-stat-card .card-body {
    padding: 1rem;
  }

  .card-header .input-icon-end {
    width: 100%;
    min-width: 0 !important;
    margin-top: 0.75rem;
  }

  .custom-datatable-filter.table-responsive {
    margin-left: -1rem;
    margin-right: -1rem;
    padding-left: 1rem;
    padding-right: 1rem;
    width: calc(100% + 2rem);
  }
}
</style>


